import React, { useMemo } from 'react';

import { Box } from '@rbilabs/component-library';
import { useIntl } from 'react-intl';

import { IHistoricalOrder } from '@rbi-ctg/menu';
import ActionButton, {
  ActionButtonSizes,
  ActionButtonVariants,
  ActionLink,
} from 'components/action-button';
import Currency from 'components/currency';
import LoadingAnimation from 'components/loading-animation';
import { useRefundEligibility } from 'hooks/refund-eligibility';
import { useOrderDetails } from 'hooks/use-order-details';
import { useSupportData } from 'hooks/use-support-data';
import LoadingIndicator from 'pages/store-locator/loading-indicator';
import { useFlag } from 'state/launchdarkly';
import { CustomEventNames, useMParticleContext } from 'state/mParticle';
import { ServiceMode, useOrderContext } from 'state/order';
import { useOrderTimedFire } from 'state/order/hooks/use-order-timed-fire';
import { useReorder } from 'state/order/hooks/use-reorder';
import { useUIContext } from 'state/ui';
import { computeDeliveryFee } from 'utils/cart/helper';
import { LaunchDarklyFlag } from 'utils/launchdarkly';

import { CartEntry } from './cart-entry';
import IsEnRouteModal from './is-en-route-modal';
import { messages } from './messages';
import {
  Block,
  Container,
  Data,
  FeeDeliveryItem,
  Footer,
  Header,
  Label,
  LoadingContainer,
  ModalHead,
  ModalInner,
  NeedHelpButtonLoadingIndicatorWrapper,
  OrderDetailsScrollContainer,
  Row,
  SpacedRow,
  StyledModal,
  TicketNumber,
} from './styled';
import { useHelpLinkUrlGenerator } from './use-help-link-url-generator';

import { MODAL_TYPE } from '.';

interface IOrderUiProps {
  order: IHistoricalOrder;
}

interface IOrderUiPropsWithFormatter extends IOrderUiProps {
  currencyFormatter: Function;
}

const OrderHeader = ({ order }: IOrderUiProps) => {
  const { formatMessage, formatTime } = useIntl();
  const { pickupDate } = useOrderTimedFire({ serverOrder: order });
  const { formattedDate, formattedTime } = useMemo(() => {
    const orderDate = new Date(order.createdAt);
    const formattedOrderDate = orderDate.toLocaleString(formatMessage(messages.localeDateTime), {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const formattedOrderTime = orderDate.toLocaleString('en-us', {
      hour: 'numeric',
      minute: 'numeric',
      timeZoneName: 'short',
    });

    return { formattedDate: formattedOrderDate, formattedTime: formattedOrderTime };
  }, [formatMessage, order.createdAt]);

  const { storeAddress, serviceMode, ticketNumber } = order.cart;
  const localizedServiceMode = {
    [ServiceMode.EAT_IN]: formatMessage(messages.dineIn),
    [ServiceMode.TAKEOUT]: formatMessage(messages.pickUp),
    [ServiceMode.DRIVE_THRU]: formatMessage(messages.driveThru),
    [ServiceMode.DELIVERY]: formatMessage(messages.delivery),
    [ServiceMode.CURBSIDE]: formatMessage(messages.curbside),
  }[serviceMode];

  return (
    <Header>
      <Row>
        <Label>{formattedDate}</Label>
      </Row>

      <Row>
        <Label>{formatMessage(messages.orderNumber)}:</Label>
        <Data>
          <TicketNumber>{ticketNumber}</TicketNumber>
        </Data>
      </Row>

      <Row>
        <Label>{formatMessage(messages.orderTime)}:</Label>
        <Data>
          <p>{formattedTime}</p>
        </Data>
      </Row>

      <Row>
        <Label>
          {formatMessage(messages.method, {
            serviceMode: '', // set service mode below for proper formatting in grid
          })}
        </Label>
        <Data>
          <p>{localizedServiceMode}</p>
        </Data>
      </Row>

      {!!order.fireOrderIn && (
        <Row>
          <Label>{formatMessage(messages.pickUpTime)}:</Label>
          <Data>
            <p>{formatTime(pickupDate)}</p>
          </Data>
        </Row>
      )}

      <Row>
        <Label>{formatMessage(messages.location)}:</Label>
        <Data>
          <p>{storeAddress.addressLine1}</p>
          {storeAddress.addressLine2 && (
            <p>{formatMessage({ id: 'unitNumber' }, { unitNumber: storeAddress.addressLine2 })}</p>
          )}
          <p>{storeAddress.city}</p>
          <p>{storeAddress.state}</p>
          <p>{storeAddress.zip}</p>
        </Data>
      </Row>
    </Header>
  );
};

const OrderFooter = ({ order, currencyFormatter }: IOrderUiPropsWithFormatter) => {
  const { delivery } = order;
  const { subTotalCents, taxCents, totalCents } = order.cart;
  const { formatMessage } = useIntl();

  const feeCents = delivery?.feeCents || 0;
  const feeDiscountCents = delivery?.feeDiscountCents || 0;
  const serviceFeeCents = delivery?.serviceFeeCents || 0;
  const smallCartFeeCents = delivery?.smallCartFeeCents || 0;

  const deliveryFeeCents = computeDeliveryFee({
    feeCents,
    feeDiscountCents,
    serviceFeeCents,
    smallCartFeeCents,
  });
  const isFreeDelivery = deliveryFeeCents === 0;

  return (
    <>
      {order.delivery && (
        <>
          <SpacedRow>
            <p>{formatMessage(messages.deliveryFee)}:</p>
            {isFreeDelivery ? (
              <FeeDeliveryItem>
                {formatMessage(messages.free)}
                &nbsp;
                <s>
                  <Currency amount={feeDiscountCents} />
                </s>
              </FeeDeliveryItem>
            ) : (
              <p>{currencyFormatter(deliveryFeeCents)}</p>
            )}
          </SpacedRow>
          {serviceFeeCents > 0 && (
            <SpacedRow>
              <p>{formatMessage(messages.serviceFee)}:</p>
              <p>{currencyFormatter(serviceFeeCents)}</p>
            </SpacedRow>
          )}
          {smallCartFeeCents > 0 && (
            <SpacedRow>
              <p>{formatMessage(messages.smallCartFee)}:</p>
              <p>{currencyFormatter(smallCartFeeCents)}</p>
            </SpacedRow>
          )}
          <SpacedRow>
            <p>{formatMessage(messages.tip)}:</p>
            <p>{currencyFormatter(order.delivery.tipCents)}</p>
          </SpacedRow>
        </>
      )}
      <SpacedRow>
        <p>{formatMessage(messages.subtotal)}</p>
        <p>{currencyFormatter(subTotalCents)}</p>
      </SpacedRow>
      <SpacedRow>
        <p>{formatMessage(messages.tax)}</p>
        <p>{currencyFormatter(taxCents)}</p>
      </SpacedRow>
      {/* TODO Add bag fee here when available from backend. */}
      <SpacedRow total>
        <p>
          <b>{formatMessage(messages.total)}</b>
        </p>
        <p>
          <b>{currencyFormatter(totalCents)}</b>
        </p>
      </SpacedRow>
    </>
  );
};

const DetailsModal: React.FC<{
  rbiOrderId: string;
  onModalDismiss: (orderId: string) => void;
  type: string;
  setModalType: (type: string) => void;
}> = ({ rbiOrderId, onModalDismiss, type, setModalType }) => {
  const { formatMessage } = useIntl();
  const { formatCurrencyForLocale } = useUIContext();
  const { order } = useOrderDetails(rbiOrderId);
  const { handleReorderClick, disabled } = useReorder(order);
  const {
    reorder: { reordering },
  } = useOrderContext();
  const enableOrderSupport = useFlag(LaunchDarklyFlag.ENABLE_ORDER_SUPPORT);

  const { logNavigationClick } = useMParticleContext();
  const { isDeliveryActive } = useRefundEligibility({
    orderId: rbiOrderId,
  });
  const { isSupportDataLoading } = useSupportData();

  const helpLink = useHelpLinkUrlGenerator(rbiOrderId);

  if (isDeliveryActive && type !== MODAL_TYPE.SHOW_DETAILS) {
    return <IsEnRouteModal rbiOrderId={rbiOrderId} onModalDismiss={() => onModalDismiss('')} />;
  }

  const modalHeading = formatMessage(messages.orderDetailsHeading);

  const handleNeedHelpClick = () => {
    logNavigationClick(CustomEventNames.BUTTON_CLICK_RECENT_ORDER_NEED_HELP);
  };

  return !order ? null : (
    <StyledModal
      onDismiss={() => onModalDismiss('')}
      mParticleEventData={{
        modalAppearanceEventMessage: 'Order Details',
      }}
    >
      <ModalInner>
        <ModalHead>
          <h2 id="modal-heading">{modalHeading}</h2>
        </ModalHead>
        <OrderDetailsScrollContainer>
          <Container>
            {order ? (
              <>
                <Block>
                  <OrderHeader order={order} />
                </Block>
                <Block>
                  {order.cart.cartEntries.map((entry, i) => (
                    <CartEntry
                      currencyFormatter={formatCurrencyForLocale}
                      entry={entry}
                      key={entry.name! + i}
                    />
                  ))}
                </Block>
                <Block>
                  <OrderFooter currencyFormatter={formatCurrencyForLocale} order={order} />
                </Block>
              </>
            ) : (
              <LoadingContainer>
                <LoadingAnimation fillColor={Styles.color.black} />
              </LoadingContainer>
            )}
          </Container>
        </OrderDetailsScrollContainer>
        <Footer>
          <Container>
            {isDeliveryActive ? (
              <ActionButton fullWidth onClick={() => setModalType(MODAL_TYPE.TRACK_ORDER)}>
                {formatMessage(messages.trackOrder)}
              </ActionButton>
            ) : (
              <ActionButton
                fullWidth
                onClick={handleReorderClick}
                isLoading={reordering}
                disabled={disabled}
              >
                {formatMessage(messages.reorder)}
              </ActionButton>
            )}
            <p>
              {formatMessage(
                messages.pricesDiscountsAndAvailabilityAreSbujectToChangeWhenYouReorder
              )}
            </p>

            {isSupportDataLoading && enableOrderSupport && (
              <NeedHelpButtonLoadingIndicatorWrapper>
                <LoadingIndicator />
              </NeedHelpButtonLoadingIndicatorWrapper>
            )}

            {/*
             * isSupportDataLoading becomes false quickly, so aria-live="polite" on this button is distracting.
             * This will silently appear for screen readers.
             */}
            {!isSupportDataLoading && enableOrderSupport && (
              <Box margin="auto">
                <ActionLink
                  fullWidth
                  variant={ActionButtonVariants.OUTLINE}
                  size={ActionButtonSizes.SMALL}
                  onClick={handleNeedHelpClick}
                  to={helpLink}
                >
                  {formatMessage(messages.needHelpButtonText)}
                </ActionLink>
              </Box>
            )}
          </Container>
        </Footer>
      </ModalInner>
    </StyledModal>
  );
};

export default DetailsModal;
